import React, { useState } from 'react';
import { getRpeLabel, getSessionRatingLabel } from './workoutHelpers';
import { MuscleMap } from '../MuscleMap';

// =============================================
// Active-workout flow modals — extracted verbatim from
// ActiveWorkoutInterface.js (Phase A decomposition). No behavior / testid change.
// =============================================

// PART 3: Last Set RPE Modal (Step 1 of 3) — technical RPE for the final set
export const LastSetRpeModal = ({ exerciseName, onSubmit, onClose }) => {
  const [selectedRpe, setSelectedRpe] = useState(null);
  const rpeLabel = getRpeLabel(selectedRpe);

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 99999 }} data-testid="last-set-rpe-modal">
      <div className="glass-card p-8 max-w-lg w-full max-h-[90dvh] overflow-y-auto my-auto">
        <h2 className="text-2xl font-bold text-white mb-2 text-center">
          💪 Última serie completada
        </h2>
        <p className="text-blue-200 text-center mb-6">
          {exerciseName}
        </p>

        <h3 className="text-lg font-semibold text-white mb-4 text-center">
          ¿Cómo de duro ha sido este último set?
        </h3>

        {/* RPE Scale - Same as rest timer RPE */}
        <div className="flex justify-center gap-1 mb-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rpe) => {
            const rpeLabelItem = getRpeLabel(rpe);
            return (
              <button
                key={rpe}
                onClick={() => setSelectedRpe(rpe)}
                data-testid={`last-set-rpe-${rpe}`}
                className={`w-10 h-12 rounded text-sm font-bold transition-all ${
                  selectedRpe === rpe
                    ? 'ring-2 ring-white scale-110 shadow-lg'
                    : ''
                } ${
                  rpeLabelItem.color === 'green' ? 'bg-green-500 text-white hover:bg-green-400' :
                  rpeLabelItem.color === 'yellow' ? 'bg-yellow-500 text-white hover:bg-yellow-400' :
                  rpeLabelItem.color === 'orange' ? 'bg-orange-500 text-white hover:bg-orange-400' :
                  'bg-red-500 text-white hover:bg-red-400'
                }`}
              >
                {rpe}
              </button>
            );
          })}
        </div>

        {/* Dynamic feedback text */}
        {rpeLabel ? (
          <div className="mt-4 p-4 rounded-lg bg-black/30 text-center">
            <p className={`text-xl font-bold ${
              rpeLabel.color === 'green' ? 'text-green-300' :
              rpeLabel.color === 'yellow' ? 'text-yellow-300' :
              rpeLabel.color === 'orange' ? 'text-orange-300' :
              'text-red-300'
            }`}>
              {rpeLabel.text}
            </p>
            <p className="text-white/70 text-sm mt-1">
              {rpeLabel.helper}
            </p>
          </div>
        ) : (
          <p className="text-blue-300 text-sm text-center">
            Selecciona un número para valorar tu esfuerzo
          </p>
        )}

        <button
          onClick={() => onSubmit(selectedRpe)}
          disabled={!selectedRpe}
          data-testid="last-set-rpe-submit"
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selectedRpe ? '✅ Continuar' : 'Selecciona tu esfuerzo'}
        </button>
      </div>
    </div>
  );
};

// Session Closing Notice Modal (Step 2 of 3) — informational transition
export const SessionClosingNoticeModal = ({ onContinue }) => {
  return (
    <div className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 99999 }} data-testid="session-closing-notice-modal">
      <div className="glass-card p-8 max-w-lg w-full text-center max-h-[90dvh] overflow-y-auto my-auto">
        <div className="text-6xl mb-6">🏁</div>
        <h2 className="text-2xl font-bold text-white mb-4">
          Estás a punto de finalizar el entrenamiento
        </h2>
        <p className="text-blue-200 text-lg mb-8">
          Un último paso antes de guardar tu sesión
        </p>
        <button
          onClick={onContinue}
          data-testid="closing-notice-continue"
          className="btn-primary w-full text-lg py-4"
        >
          Continuar →
        </button>
      </div>
    </div>
  );
};

// Global Session Rating Modal (Step 3 of 3) — experience-based rating, NOT RPE
export const SessionRatingModal = ({ sessionRating, setSessionRating, sessionNotes, setSessionNotes, exerciseFeedback, onSubmit }) => {
  const ratingLabel = getSessionRatingLabel(sessionRating);

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 99999 }} data-testid="session-rating-modal">
      <div className="glass-card p-8 max-w-2xl w-full max-h-[90dvh] overflow-y-auto my-auto">
        <h2 className="text-3xl font-bold text-white mb-2 text-center">
          🎉 ¡Entrenamiento completado!
        </h2>

        {/* Global Session Rating - Experience based, NOT RPE */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-2 text-center">
            ¿Cómo valorarías la sesión en general?
          </h3>
          <p className="text-blue-300 text-sm text-center mb-4">
            Valora tu experiencia global del entrenamiento
          </p>

          {/* Rating scale 1-10 with experience-based colors */}
          <div className="flex justify-center space-x-2 mb-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => {
              const label = getSessionRatingLabel(rating);
              return (
                <button
                  key={rating}
                  onClick={() => setSessionRating(rating)}
                  data-testid={`session-rating-${rating}`}
                  className={`w-11 h-12 rounded-lg font-bold text-lg transition-all duration-200 ${
                    sessionRating === rating
                      ? 'ring-2 ring-white scale-110 shadow-lg'
                      : ''
                  } ${
                    label.color === 'red' ? 'bg-red-500 text-white hover:bg-red-400' :
                    label.color === 'orange' ? 'bg-orange-500 text-white hover:bg-orange-400' :
                    label.color === 'yellow' ? 'bg-yellow-500 text-white hover:bg-yellow-400' :
                    'bg-green-500 text-white hover:bg-green-400'
                  }`}
                >
                  {rating}
                </button>
              );
            })}
          </div>

          {/* Dynamic session feedback - experience based */}
          {ratingLabel ? (
            <div className="mt-3 p-3 rounded-lg bg-black/30 text-center">
              <p className={`text-lg font-bold ${
                ratingLabel.color === 'red' ? 'text-red-300' :
                ratingLabel.color === 'orange' ? 'text-orange-300' :
                ratingLabel.color === 'yellow' ? 'text-yellow-300' :
                'text-green-300'
              }`}>
                {ratingLabel.text}
              </p>
              <p className="text-white/70 text-sm mt-1">
                {ratingLabel.helper}
              </p>
            </div>
          ) : (
            <p className="text-center text-blue-300 text-sm">
              Toca un número para valorar tu sesión
            </p>
          )}
        </div>

        {/* Exercise Feedback Summary */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-white mb-3 text-center">
            Tu opinión sobre los ejercicios:
          </h4>
          {(() => {
            const feedbackItems = Object.entries(exerciseFeedback).filter(([id, feedback]) => feedback);
            const favorites = feedbackItems.filter(([id, f]) => f === 'favorite').length;
            const dislikes = feedbackItems.filter(([id, f]) => f === 'dislike').length;

            if (feedbackItems.length === 0) {
              return (
                <p className="text-blue-300 text-sm text-center italic">
                  No has dado feedback sobre ningún ejercicio
                </p>
              );
            }

            return (
              <>
                {/* Feedback Count Summary */}
                <div className="flex justify-center gap-6 mb-4">
                  {favorites > 0 && (
                    <div className="flex items-center gap-2 text-green-300">
                      <span className="text-2xl">👍</span>
                      <span className="font-semibold">×{favorites}</span>
                    </div>
                  )}
                  {dislikes > 0 && (
                    <div className="flex items-center gap-2 text-red-300">
                      <span className="text-2xl">👎</span>
                      <span className="font-semibold">×{dislikes}</span>
                    </div>
                  )}
                </div>

                {/* Emoji Grid Display */}
                <div className="flex flex-wrap justify-center gap-3">
                  {feedbackItems.map(([exerciseId, feedback]) => (
                    <div
                      key={exerciseId}
                      className={`p-4 rounded-lg hover:scale-110 transition-transform ${
                        feedback === 'favorite' ? 'bg-green-900/30' : 'bg-red-900/30'
                      }`}
                    >
                      <span className="text-5xl">
                        {feedback === 'favorite' ? '👍' : '👎'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* Written feedback — client's own words about the session */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-white mb-2 text-center">
            ¿Quieres añadir algún comentario?
          </h4>
          <p className="text-blue-300 text-sm text-center mb-3">
            Cuéntale a tu entrenador cómo te sentiste (opcional)
          </p>
          <textarea
            value={sessionNotes ?? ''}
            onChange={(e) => setSessionNotes(e.target.value.slice(0, 1000))}
            maxLength={1000}
            rows={3}
            data-testid="session-notes-input"
            placeholder="Ej: me costó el último ejercicio, molestia leve en el hombro, buen día de energía…"
            className="w-full rounded-lg bg-black/30 border border-blue-400/30 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none"
          />
          <p className="text-right text-xs text-slate-500 mt-1">{(sessionNotes || '').length}/1000</p>
        </div>

        {/* Submit Button */}
        <button
          onClick={onSubmit}
          disabled={sessionRating === 0}
          data-testid="session-rating-submit"
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sessionRating === 0 ? 'Por favor, valora tu sesión' : '✅ Enviar y finalizar entrenamiento'}
        </button>
      </div>
    </div>
  );
};

// Workout Summary Modal (Step 4 of 4) — key session metrics before returning
export const WorkoutSummaryModal = ({ summaryData, onContinue }) => {
  const hasMuscles = Array.isArray(summaryData?.exercises) && summaryData.exercises.length > 0;
  const [tab, setTab] = useState(hasMuscles ? 'muscles' : 'summary');
  if (!summaryData) return null;

  const {
    duration,
    effectiveTime,
    totalExercises,
    totalSetsCompleted,
    totalVolume,
    supersetsCompleted,
    prsAchieved,
    exercises,
    clientId,
    session_notes: sessionNotes
  } = summaryData;

  // Format duration as minutes (never show "0 min" when real time elapsed)
  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0 min';
    const mins = Math.round(seconds / 60);
    if (mins < 1) return '<1 min';
    return `${mins} min`;
  };

  // Format volume with thousands separator
  const formatVolume = (vol) => {
    if (!vol || vol < 0) return '0 kg';
    return `${vol.toLocaleString('es-ES')} kg`;
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ zIndex: 99999 }}
      data-testid="workout-summary-modal"
    >
      <div className="glass-card p-8 max-w-md w-full text-center max-h-[90dvh] overflow-y-auto my-auto">
        {/* Header */}
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-3xl font-bold text-white mb-2">
          ¡Entrenamiento Completado!
        </h2>
        <p className="text-blue-300 mb-6">
          Resumen de tu sesión
        </p>

        {hasMuscles && (
          <div className="flex justify-center gap-2 mb-6" data-testid="summary-tabs">
            {[['muscles', '💪 Músculos'], ['summary', '📊 Resumen']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                data-testid={`summary-tab-${key}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === key ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {hasMuscles && tab === 'muscles' && (
          <MuscleMap exercises={exercises} clientId={clientId} language="es" className="mb-8 text-left" shareable shareTitle={summaryData.workoutName || 'Mi entrenamiento'} shareDate={new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })} />
        )}

        {/* Metrics Grid */}
        <div className={`grid grid-cols-2 gap-4 mb-8 ${hasMuscles && tab !== 'summary' ? 'hidden' : ''}`}>
          {/* Duration */}
          <div className="bg-blue-900/30 rounded-xl p-4" data-testid="summary-duration">
            <div className="text-3xl mb-1">⏱️</div>
            <div className="text-2xl font-bold text-white">{formatDuration(duration)}</div>
            <div className="text-sm text-blue-300">Duración</div>
          </div>

          {/* Effective training time */}
          {effectiveTime != null && (
            <div className="bg-emerald-900/30 rounded-xl p-4" data-testid="summary-effective">
              <div className="text-3xl mb-1">💪</div>
              <div className="text-2xl font-bold text-white">{formatDuration(effectiveTime)}</div>
              <div className="text-sm text-emerald-300">Tiempo efectivo</div>
            </div>
          )}

          {/* Exercises */}
          <div className="bg-green-900/30 rounded-xl p-4" data-testid="summary-exercises">
            <div className="text-3xl mb-1">💪</div>
            <div className="text-2xl font-bold text-white">{totalExercises}</div>
            <div className="text-sm text-green-300">Ejercicios</div>
          </div>

          {/* Sets Completed */}
          <div className="bg-purple-900/30 rounded-xl p-4" data-testid="summary-sets">
            <div className="text-3xl mb-1">✅</div>
            <div className="text-2xl font-bold text-white">{totalSetsCompleted}</div>
            <div className="text-sm text-purple-300">Series completadas</div>
          </div>

          {/* Total Volume */}
          <div className="bg-orange-900/30 rounded-xl p-4" data-testid="summary-volume">
            <div className="text-3xl mb-1">🏋️</div>
            <div className="text-2xl font-bold text-white">{formatVolume(totalVolume)}</div>
            <div className="text-sm text-orange-300">Volumen total</div>
          </div>
        </div>

        {/* Optional: Supersets and PRs row */}
        {(supersetsCompleted > 0 || prsAchieved > 0) && (!hasMuscles || tab === 'summary') && (
          <div className="flex justify-center gap-6 mb-8">
            {supersetsCompleted > 0 && (
              <div className="bg-cyan-900/30 rounded-xl px-6 py-3" data-testid="summary-supersets">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔗</span>
                  <div>
                    <div className="text-xl font-bold text-white">{supersetsCompleted}</div>
                    <div className="text-xs text-cyan-300">Supersets</div>
                  </div>
                </div>
              </div>
            )}
            {prsAchieved > 0 && (
              <div className="bg-yellow-900/30 rounded-xl px-6 py-3" data-testid="summary-prs">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏆</span>
                  <div>
                    <div className="text-xl font-bold text-white">{prsAchieved}</div>
                    <div className="text-xs text-yellow-300">PRs logrados</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Client's written feedback recap */}
        {sessionNotes && sessionNotes.trim() && (
          <div className="bg-blue-900/20 border border-blue-400/20 rounded-xl p-4 mb-8 text-left" data-testid="summary-notes">
            <p className="text-blue-300 text-xs font-medium mb-1">📝 Tu comentario</p>
            <p className="text-white text-sm whitespace-pre-wrap break-words">{sessionNotes}</p>
          </div>
        )}

        {/* Continue Button */}
        <button
          onClick={onContinue}
          data-testid="summary-continue-btn"
          className="btn-primary w-full text-lg py-4"
        >
          Continuar →
        </button>
      </div>
    </div>
  );
};

// Exit Confirmation Modal (P0 — Data Loss Prevention)
export const ExitConfirmationModal = ({ onConfirm, onCancel, language }) => {
  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ zIndex: 999999 }}
      data-testid="exit-confirmation-modal"
    >
      <div className="glass-card p-8 max-w-lg w-full text-center max-h-[90dvh] overflow-y-auto my-auto">
        <div className="text-6xl mb-6">⚠️</div>
        <h2 className="text-2xl font-bold text-white mb-4">
          {language === 'es'
            ? '¿Seguro que quieres salir?'
            : 'Are you sure you want to leave?'}
        </h2>
        <p className="text-red-300 text-lg mb-8">
          {language === 'es'
            ? 'Tienes cambios sin guardar. Si sales ahora, perderás todos los datos de esta sesión de entrenamiento.'
            : 'You have unsaved changes. If you leave this training session now, your data will be lost.'}
        </p>
        <div className="flex gap-4">
          <button
            onClick={onCancel}
            data-testid="exit-cancel-btn"
            className="btn-secondary flex-1 py-4 text-lg"
          >
            {language === 'es' ? '← Continuar entrenando' : '← Continue training'}
          </button>
          <button
            onClick={onConfirm}
            data-testid="exit-confirm-btn"
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-6 rounded-lg flex-1 text-lg transition"
          >
            {language === 'es' ? '🚪 Salir y perder datos' : '🚪 Leave & lose data'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Real-time PR celebration toast — fires the moment a client beats their record
// during the live session. Non-blocking, auto-dismisses. Client-facing Spanish.
export const PrCelebrationToast = ({ celebration }) => {
  if (!celebration) return null;
  const { exerciseName, weight, reps, setNumber, bodyweight } = celebration;
  return (
    <div
      className="fixed top-4 inset-x-0 flex justify-center px-4 pointer-events-none"
      style={{ zIndex: 100000 }}
      data-testid="pr-celebration-toast"
    >
      <div className="bounce-in pointer-events-auto max-w-sm w-full rounded-2xl border border-yellow-300/50 bg-gradient-to-br from-amber-500 to-yellow-600 shadow-2xl shadow-amber-500/40 px-5 py-4 text-center">
        <div className="text-3xl mb-1">🏆</div>
        <div className="text-white font-extrabold text-lg leading-tight">¡Nuevo récord!</div>
        <div className="text-yellow-50/90 text-sm font-medium mt-0.5">{exerciseName}</div>
        <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/25 px-3 py-1">
          <span className="text-yellow-100 font-mono font-bold text-sm" data-testid="pr-celebration-detail">
            serie #{setNumber} · {(bodyweight || !weight) ? `${reps} reps` : `${weight} kg × ${reps} reps`}
          </span>
        </div>
      </div>
    </div>
  );
};

